import { useState, useEffect } from 'react';
import { CircleOff, MoreVertical } from 'lucide-react';
import CustomAppBar from '../../../mainComponents/appBar';
import {
  useBlockUserMutation,
  useGetBlockedUsersQuery,
  useUnblockUserMutation,
} from '../../../redux/setting';
import toast from 'react-hot-toast';

export default function BlockedUsers() {
  const [inputId, setInputId] = useState('');
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [confirmUnblockId, setConfirmUnblockId] = useState<number | null>(null);

  const [blockUser, { isLoading: blocking }] = useBlockUserMutation();
  const [unblockUser, { isLoading: unblocking }] = useUnblockUserMutation();
  const [blockReason, setBlockReason] = useState('');


  const {
    data,
    isLoading: loadingList,
    isError,
  } = useGetBlockedUsersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    console.log('Blocked users data:', data);
  }, [data]);

  const blockedUsers = Array.isArray(data) ? data : [];

const handleBlock = async (id: number, reason: string) => {
  if (!reason.trim()) {
    toast.error('Please provide a reason for blocking.');
    return;
  }

  try {
    await blockUser({ id, reason }).unwrap();
    toast.success('User blocked successfully!');
    setInputId('');
    setBlockReason('');
  } catch (error: any) {
    toast.error(error?.data?.message || 'Failed to block user');
  }
};

  const handleConfirmUnblock = async () => {
    if (!confirmUnblockId) return;
    try {
      await unblockUser(Number(confirmUnblockId)).unwrap();
      toast.success('User unblocked!');
      setActiveMenu(null);
      setConfirmUnblockId(null);
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to unblock user');
    }
  };

  return (
    <div
      className={`flex justify-center pt-10 bg-[#f5f5f5] h-screen py-6 text-black font-[SF Pro Display] ${confirmUnblockId ? 'backdrop-blur-sm bg-blue-100/30' : ''
        }`}
    >
      <div className="w-full max-w-sm">
        <CustomAppBar title="Blocked Users" />

        <div className="bg-white rounded-2xl p-6 mt-5 text-center shadow-md space-y-6 relative">
          {loadingList ? (
            <p className="text-gray-500 text-sm">Loading blocked users...</p>
          ) : isError ? (
            <p className="text-red-500 text-sm">Failed to load blocked users.</p>
          ) : blockedUsers.length === 0 ? (
            <div>
              <div className="flex justify-center mb-4">
                <div className="bg-red-100 p-4 rounded-full">
                  <CircleOff className="w-6 h-6 text-red-500" />
                </div>
              </div>
              <h2 className="text-lg font-bold mb-1">No users blocked 😇</h2>
              <p className="text-sm text-gray-600">
                You haven't blocked anyone... yet. If someone's being sus, block them here.
              </p>
            </div>
          ) : (
            <ul className="mt-2 space-y-2 text-left">
              {blockedUsers.map((user: any) => (
                <li
                  key={user.id}
                  className="relative flex items-center justify-between text-sm bg-gray-100 px-4 py-2 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <img
                      src={
                        user.profile_picture && user.profile_picture !== 'null' && user.profile_picture !== ''
                          ? user.profile_picture
                          : '/placeholder-avatar.png'
                      }
                      alt="profile"
                      className="w-8 h-8 rounded-full object-cover bg-gray-200"
                    />
                    <span>@{user.username}</span>
                  </div>

                  <button onClick={() => setActiveMenu(activeMenu === user.id ? null : user.id)}>
                    <MoreVertical className="w-4 h-4 text-gray-500" />
                  </button>

                  {activeMenu === user.id && (
                    <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border z-50 w-40">
                      <button
                        onClick={() => {
                          setActiveMenu(null);
                          setConfirmUnblockId(user.id);
                        }}
                        className="block w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-gray-50 rounded-t-xl"
                        disabled={unblocking}
                      >
                        {unblocking ? 'Unblocking...' : 'Unblock User'}
                      </button>
                      <button
                        onClick={() => setActiveMenu(null)}
                        className="block w-full text-left px-4 py-3 text-sm text-gray-500 hover:bg-gray-50 rounded-b-xl"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4">
            <input
              type="number"
              placeholder="Enter User ID to block"
              value={inputId}
              onChange={(e) => setInputId(e.target.value)}
              className="mb-2 w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />

            <input
              type="text"
              placeholder="Reason for blocking (optional)"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              className="mb-2 w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />

            <button
             onClick={() => handleBlock(Number(inputId), blockReason)}
              disabled={blocking || !inputId}
              className="bg-red-500 text-white w-full py-2 rounded-full font-semibold text-sm disabled:opacity-50"
            >
              {blocking ? 'Blocking...' : 'Block User'}
            </button>

          </div>
        </div>

        {/* Unblock Confirmation Modal */}
        {confirmUnblockId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-lg w-[90%] max-w-xs p-6 text-center">
              <div className="mb-4">
                <h2 className="text-lg font-bold text-black">Are you sure?</h2>
                <p className="text-sm text-gray-600 mt-1">
                  You are about to unblock{' '}
                  <span className="font-semibold text-red-500">
                    @{blockedUsers.find((u) => u.id.toString() === confirmUnblockId.toString())?.username}
                  </span>
                </p>
              </div>
              <div className="flex items-center justify-between">
                <button
                  className="bg-gray-200 text-black text-sm font-semibold rounded-full px-5 py-2 w-[45%]"
                  onClick={() => setConfirmUnblockId(null)}
                >
                  Cancel
                </button>
                <button
                  className="bg-red-500 text-white text-sm font-semibold rounded-full px-5 py-2 w-[45%]"
                  onClick={handleConfirmUnblock}
                >
                  {unblocking ? 'Unblocking...' : 'Unblock'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}













